<template>
  <Popup :ref="(el) => popup = el"
         @onClose="hide"
         :title="$t('button.payout_settings')"
  >
    <form class="requisites">
      <div class="requisites__container">

        <div class="requisites__block">
          <div class="requisites__label requisites__label--start text-capitalize-first">
            {{ $t("labels.from_sum") }}
          </div>
          <div class="requisites__content">
            <FormInputWrapper v-slot="parentProps" field="minAmount" form="settings">
              <input :class="parentProps.class" v-model="form.minAmount" type="text" class="form-control " id="from_sum">
            </FormInputWrapper>
          </div>
        </div>

        <div class="requisites__block">
          <div class="requisites__label requisites__label--start text-capitalize-first">
            {{ $t("labels.to_sum") }}
          </div>
          <div class="requisites__content">
            <FormInputWrapper v-slot="parentProps" field="maxAmount" form="settings">
              <input :class="parentProps.class" v-model="form.maxAmount" type="text" class="form-control " id="to_sum">
            </FormInputWrapper>
          </div>
        </div>

        <div class="requisites__block">
          <div class="requisites__label requisites__label--start text-capitalize-first">
            {{ $t("labels.currencies") }}
          </div>
          <div class="requisites__content">
            <FormInputWrapper v-slot="parentProps" form="settings" field="currencies">
              <Multiselect id="pay-out-currencies"
                           class="multiselect-sm"
                           v-model="form.currencies"
                           :options="currencyList"
                           :custom-label="(opt) => `${opt}`"
                           :multiple="true"
                           select-label="Select"
                           :placeholder="$t('labels.currencies')"
              >
              </Multiselect>
            </FormInputWrapper>
          </div>
        </div>

        <div class="requisites__block requisites__block--switcher">
          <div class="requisites__label text-capitalize-first">{{ $t('labels.rounded_to_1000')}}</div>
          <div class="requisites__content requisites__content__switch">
            <label class="switch">
              <input type="checkbox" v-model="form.multiplesOfValue1000">
              <span class="slider"></span>
            </label>
          </div>
        </div>

        <div class="requisites__block">
          <div class="requisites__label requisites__label--start text-capitalize-first">
            {{ $t("labels.payment_methods") }}
          </div>
          <div class="requisites__content">
            <FormInputWrapper v-slot="parentProps" form="settings" field="paymentMethods">
              <Multiselect id="pay-out-methods"
                           class="multiselect-sm"
                           v-model="form.paymentMethods"
                           :options="methodList()"
                           :custom-label="(opt) => $t(`pay_methods.${opt}`)"
                           :multiple="true"
                           :placeholder="helper.trans('labels.payment_methods')"
                           select-label="Select"
              >
                <template #option="props">
                  <div class="pay-method">
                    <PayMethodIcon icon-class="icon" :method="props.option"></PayMethodIcon>
                    <span class="overflow-hidden" style="white-space: nowrap; text-overflow: ellipsis">{{ helper.trans('pay_methods.' + props.option) }}</span>
                  </div>
                </template>
              </Multiselect>
            </FormInputWrapper>
          </div>
        </div>

        <div class="requisites__block">
          <div class="requisites__label requisites__label--start text-capitalize-first">
            {{ $t("labels.payment_options") }}
          </div>
          <div class="requisites__content">
            <FormInputWrapper v-slot="parentProps" form="settings" field="paymentOptions">
              <Multiselect id="pay-out-options"
                           class="multiselect-sm"
                           v-model="form.paymentOptions"
                           :options="store.getters['currencyMethods/options']()"
                           :custom-label="(opt) => helper.trans(`payment_options.${opt}`)"
                           :multiple="true"
                           select-label="Select"
                           :placeholder="helper.trans('labels.payment_options')"
              >
              </Multiselect>
            </FormInputWrapper>
          </div>
        </div>

        <div class="requisites__block requisites__block--switcher">
          <div class="requisites__label text-capitalize-first">{{ $t('labels.only_mir_cards')}}</div>
          <div class="requisites__content requisites__content__switch">
            <label class="switch">
              <input type="checkbox" v-model="form.onlyMir"  :true-value="true" >
              <span class="slider"></span>
            </label>
          </div>
        </div>

      </div>

      <div class="requisites__controls">
        <button class="btn" type="button" @click.prevent="hide">
          <span class="text-capitalize-first">{{ $t('button.cancel') }}</span>
        </button>
        <LoaderButton :on-click="save" class="btn btn--accept">
          <span class="text-capitalize-first">{{ $t('button.save') }}</span>
        </LoaderButton>
      </div>
    </form>
  </Popup>
</template>
<script lang="ts">
import {computed, defineComponent, onMounted, PropType, ref, watch} from 'vue'
import {reactive, toRefs} from '@vue/reactivity';
import Popup from "@/apps/public/components/Popup.vue";
import {$t, helper} from "@/service/CommonHelper";
import LoaderButton from "@/apps/public/components/LoaderButton.vue";
import {api} from "@/service/APIClient";
import CopyButton from "@/apps/public/components/CopyButton.vue";
import {useNotification} from "@kyvg/vue3-notification";
import {useStore} from "vuex";
import FormInputWrapper from "@/components/FormInputWrapper.vue";
import Multiselect from "vue-multiselect";
import {PaymentOption} from "@/models/enum/PaymentOption";
import PayMethodIcon from "@/components/PayMethodIcon.vue";
import TraderPayoutSettingsDTO from "@/models/TraderPayoutSettingsDTO";


export default defineComponent({
  emits: ['updated'],
  methods: {$t},
  components: {
    PayMethodIcon,
    FormInputWrapper,
    LoaderButton,
    CopyButton,
    Popup,
    Multiselect
  },
  setup(props, {emit}) {
    const notification = useNotification()
    const store = useStore()
    const popup = ref<InstanceType<typeof Popup> | null>(null)
    const state = reactive({
      form: {} as TraderPayoutSettingsDTO,
    });

    const show = (dto: TraderPayoutSettingsDTO) => {
      helper.clearErrors('settings')

      state.form = dto

      popup.value?.show()
    }


    const hide = () => {
      popup.value?.hide()
    }


    const save = async () => {
      try {
        await api.traderPayoutSettingsUpdate(state.form)
        emit('updated')
        hide()
      } catch (e) {
        helper.handleErrors('settings', e)
        console.log(e);
      }
    }

    onMounted(() => {
      store.dispatch("currencyMethods/loadList");
    })

    return {
      ...toRefs(state),
      popup,
      store,
      show,
      hide,
      helper,
      save,
      PaymentOption,
      currencyList: computed(() => store.getters["currencyMethods/currencies"]),
      methodList: computed(() => store.getters["currencyMethods/methods"]),
    }
  },
  async beforeMount() {
  }
})
</script>
